/**
 * Seasonal drift. See snippets/seasonal-drift.liquid.
 *
 * Each month has three scenes, shown 10s after the first page load and then
 * 35s apart, round and round. Every scene clears the screen within 35s:
 *   October:  leaves → pumpkins → ghosts
 *   November: acorns → pumpkin pies → turkeys
 *   December: snowflakes → Santa hats → Christmas trees (all falling)
 * Everything stops after Dec 31 2026 (shopper's local time).
 *
 * The rotation and the next due time are kept in sessionStorage so the rhythm
 * carries across pages. Nothing new starts while the tab is hidden or a
 * drawer/modal is open; it retries a few seconds later.
 *
 * Review flags: ?drift=fast (every 5s), ?drift=november or ?drift=december to
 * preview a month early, combinable as ?drift=december,fast.
 */
(() => {
  const NOV_1 = new Date(2026, 10, 1).getTime();
  const DEC_1 = new Date(2026, 11, 1).getTime();
  const END = new Date(2027, 0, 1).getTime();
  if (Date.now() >= END) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const flags = new URLSearchParams(location.search)
    .getAll('drift')
    .flatMap((v) => v.split(','));
  const fast = flags.includes('fast');
  const forced = ['october', 'november', 'december'].find((m) => flags.includes(m));

  const FIRST_DELAY = fast ? 2000 : 10000;
  const INTERVAL = fast ? 5000 : 35000;
  const RETRY = 5000;
  const DUE_KEY = 'ruie-drift-due';

  const COUNT = () => (window.innerWidth < 750 ? 6 : 10);
  const STAGGER = 7000; // spread each group's starts over ~7s so they trickle in

  const rand = (min, max) => min + Math.random() * (max - min);

  /* ---------- Artwork ---------- */

  const LEAF_COLORS = ['#dbb44b', '#746643', '#402225', '#b5562a'];
  const MAPLE =
    'M12 2l1.2 3.4 2.3-1.1-.5 4.2 3.2-2.3.9 2.1 2.5-.4-1.3 3.3 1.6.9-4.6 3.9.5 1.6-4.8-.6.2 4.9h-1.4l.2-4.9-4.8.6.5-1.6-4.6-3.9 1.6-.9-1.3-3.3 2.5.4.9-2.1 3.2 2.3-.5-4.2 2.3 1.1z';
  const OVAL = 'M12 2C17 6 18.5 12 12 19 5.5 12 7 6 12 2z';

  /** Alternates maple and oval leaves, each with a stem. */
  const leafSvg = (i) =>
    `<svg viewBox="0 0 24 24"><path fill="${LEAF_COLORS[i % LEAF_COLORS.length]}" d="${i % 2 ? OVAL : MAPLE}"/><path d="M12 ${i % 2 ? 5 : 9}v14" stroke="#402225" stroke-opacity=".5" stroke-width=".9" fill="none"/></svg>`;

  const PUMPKIN_SVG = `<svg viewBox="0 0 32 32">
    <path fill="#5b4a2a" d="M15 4c1-1.5 3-2 4-1.5-1.2.8-2 2.2-2 4h-2c0-1 0-1.7 0-2.5z"/>
    <ellipse cx="10" cy="18" rx="7" ry="9" fill="#c96f24"/>
    <ellipse cx="22" cy="18" rx="7" ry="9" fill="#c96f24"/>
    <ellipse cx="16" cy="18" rx="7" ry="10" fill="#de8a35"/>
    <path fill="#402225" d="M11 15l2.5-2.5L15 15zm6 0l1.5-2.5L21 15zm-5.5 5.5c2 2 7 2 9 0-1.5.6-3 .8-4.5.8s-3-.2-4.5-.8z"/>
  </svg>`;

  const GHOST_SVG = `<svg viewBox="0 0 32 32">
    <path fill="#fdfbf5" stroke="#402225" stroke-opacity=".55" stroke-width="1" d="M16 3C9.5 3 6 8 6 14v13l3-2 3.5 3 3.5-3 3.5 3 3.5-3 3 2V14c0-6-3.5-11-10-11z"/>
    <ellipse cx="12.5" cy="13.5" rx="1.5" ry="2.2" fill="#0e0e06"/>
    <ellipse cx="19.5" cy="13.5" rx="1.5" ry="2.2" fill="#0e0e06"/>
    <ellipse cx="16" cy="19" rx="1.4" ry="1.8" fill="#0e0e06" opacity=".7"/>
  </svg>`;

  const ACORN_COLORS = ['#b07a3e', '#8f5f2e', '#c48d4c'];
  const acornSvg = (i) => `<svg viewBox="0 0 24 24">
    <path fill="${ACORN_COLORS[i % ACORN_COLORS.length]}" d="M5.6 11h12.8c0 5-2.8 9.4-6.4 11.4C8.4 20.4 5.6 16 5.6 11z"/>
    <ellipse cx="9.6" cy="14.5" rx="1.2" ry="2.4" fill="#f0ead6" opacity=".25"/>
    <path fill="#5b4a2a" d="M4 10c0-4 3.6-6 8-6s8 2 8 6c0 .8-.6 1.2-1.4 1.2H5.4C4.6 11.2 4 10.8 4 10z"/>
    <path d="M7 8.5h10M8.5 6.2h7" stroke="#402225" stroke-opacity=".45" stroke-width=".8"/>
    <path d="M12 4c0-1.4.6-2.4 1.8-3" stroke="#402225" stroke-width="1.2" stroke-linecap="round" fill="none"/>
  </svg>`;

  /** Fluted crust: bumps spaced around the rim ellipse. */
  const PIE_CRIMP = Array.from({ length: 18 }, (_, k) => {
    const t = (k / 18) * Math.PI * 2;
    return `<circle cx="${(16 + 12.6 * Math.cos(t)).toFixed(2)}" cy="${(15 + 5.6 * Math.sin(t)).toFixed(2)}" r="1.7"/>`;
  }).join('');

  const PIE_SVG = `<svg viewBox="0 0 32 32">
    <path fill="#9c6a33" d="M2 15v3.5c0 3.9 6.3 7 14 7s14-3.1 14-7V15z"/>
    <ellipse cx="16" cy="15" rx="14" ry="6.6" fill="#d9a056"/>
    <g fill="#ecc27c">${PIE_CRIMP}</g>
    <ellipse cx="16" cy="15" rx="10.8" ry="4.5" fill="#c4611d"/>
    <ellipse cx="13.5" cy="14" rx="5" ry="1.6" fill="#d8782c" opacity=".7"/>
    <g fill="#fbf6ea" stroke="#d9c9a8" stroke-width=".5">
      <ellipse cx="16" cy="14.4" rx="3.6" ry="1.7"/>
      <ellipse cx="16" cy="13" rx="2.6" ry="1.3"/>
      <path d="M14.6 12.4c.4-1.2 1.6-1.9 2.4-1.6-.3.5-.3 1 .1 1.6z"/>
    </g>
  </svg>`;

  const TURKEY_SVG = `<svg viewBox="0 0 32 32">
    <circle cx="12" cy="16" r="11" fill="#b5562a"/>
    <circle cx="12" cy="16" r="8.5" fill="#dbb44b"/>
    <circle cx="12" cy="16" r="6" fill="#746643"/>
    <path d="M14 25l-1 4.5M18 25l1 4.5M11.5 29.5h2.5M18 29.5h2.5" stroke="#c9924a" stroke-width="1.2" stroke-linecap="round"/>
    <ellipse cx="16" cy="20" rx="7" ry="6" fill="#6b4a2e"/>
    <path d="M20 18c1.6-2.4 2.6-5 2.8-8" stroke="#6b4a2e" stroke-width="3.2" stroke-linecap="round" fill="none"/>
    <circle cx="23.3" cy="9.4" r="2.6" fill="#6b4a2e"/>
    <path fill="#dbb44b" d="M25.6 8.7l2.6.8-2.6.9z"/>
    <path fill="#b3302c" d="M25.4 10.6c.9 1.2.9 2.6-.2 3.6-.7-1-.7-2.4.2-3.6z"/>
    <circle cx="23.9" cy="8.8" r=".55" fill="#f0ead6"/>
  </svg>`;

  const SNOW_COLORS = ['#8fa4b8', '#dbb44b', '#b8c6d4'];
  const SNOW_ARMS = [0, 60, 120, 180, 240, 300]
    .map((r) => `<g transform="rotate(${r} 12 12)"><path d="M12 12V2.5M9.6 4.8 12 7.2l2.4-2.4"/></g>`)
    .join('');
  const snowSvg = (i) =>
    `<svg viewBox="0 0 24 24"><g fill="none" stroke="${SNOW_COLORS[i % SNOW_COLORS.length]}" stroke-width="1.5" stroke-linecap="round">${SNOW_ARMS}</g></svg>`;

  const HAT_SVG = `<svg viewBox="0 0 32 32">
    <path fill="#b3302c" d="M6 23C7 13 13 6 21 6c4 0 6.5 3 6 7.5-1.5-2.5-4-3.5-6-2 2 3.5 3.5 7.5 4 11.5z"/>
    <path fill="#8e2522" opacity=".6" d="M21 11.5c2 3.5 3.5 7.5 4 11.5h-3c-.3-4-1.2-8-3-11z"/>
    <rect x="4" y="21.5" width="24" height="5.5" rx="2.75" fill="#f7f3e8" stroke="#402225" stroke-opacity=".3" stroke-width=".8"/>
    <circle cx="27" cy="14" r="2.8" fill="#f7f3e8" stroke="#402225" stroke-opacity=".3" stroke-width=".8"/>
  </svg>`;

  const TREE_SVG = `<svg viewBox="0 0 32 32">
    <rect x="14.5" y="25" width="3" height="4.5" fill="#5b4a2a"/>
    <path fill="#3f5b3a" d="M16 14l11 12H5z"/>
    <path fill="#4d6b43" d="M16 9l9 10H7z"/>
    <path fill="#3f5b3a" d="M16 4.5l7 8.5H9z"/>
    <circle cx="13" cy="12" r="1" fill="#b3302c"/>
    <circle cx="19" cy="16.5" r="1" fill="#dbb44b"/>
    <circle cx="11" cy="21.5" r="1.1" fill="#dbb44b"/>
    <circle cx="20.5" cy="23" r="1.1" fill="#b3302c"/>
    <circle cx="16" cy="19.5" r="1" fill="#f0ead6"/>
    <path fill="#dbb44b" d="M16 1l.9 1.9 2 .2-1.5 1.4.4 2-1.8-1-1.8 1 .4-2-1.5-1.4 2-.2z"/>
  </svg>`;

  /* ---------- Motion ---------- */

  /** @type {HTMLDivElement | null} */
  let layer = null;
  let running = false;

  function getLayer() {
    if (!layer) {
      layer = document.createElement('div');
      layer.className = 'sd-layer';
      layer.setAttribute('aria-hidden', 'true');
      document.body.appendChild(layer);
    }
    return layer;
  }

  /**
   * Adds one item to the layer and animates it along a path.
   * @param {string} kind
   * @param {string} svg
   * @param {number} size
   * @param {Keyframe[]} keyframes
   * @param {KeyframeAnimationOptions} options
   * @returns {Promise<void>}
   */
  function fly(kind, svg, size, keyframes, options) {
    const item = document.createElement('div');
    item.className = `sd-item sd-${kind}`;
    item.style.width = `${size}px`;
    item.style.height = `${size}px`;
    item.style.opacity = '0';
    item.innerHTML = `<div class="sd-inner">${svg}</div>`;
    getLayer().appendChild(item);
    const anim = item.animate(keyframes, { fill: 'both', ...options });
    return anim.finished.catch(() => {}).then(() => item.remove());
  }

  /**
   * Evenly spaced lanes (0..1) in shuffled order, so a staggered group doesn't
   * march across in a tidy diagonal.
   * @param {number} n
   */
  function lanes(n) {
    const out = Array.from({ length: n }, (_, i) => (i + rand(0.15, 0.85)) / n);
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  }

  /** @param {number} i @param {number} n @param {number} [spread] */
  const startAt = (i, n, spread = STAGGER) => (i / n) * spread + rand(0, spread / n);

  /**
   * A group falling from the top, drifting right as it spins.
   * @param {{ kind: string, svg: (i: number) => string, size: [number, number], duration: [number, number], spin: number, opacity?: number }} o
   */
  function fall(o) {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const n = COUNT();
    const peak = o.opacity ?? 0.9;
    return Promise.all(
      lanes(n).map((lane, i) => {
        const x0 = (lane * 0.9 - 0.1) * w;
        const x1 = x0 + rand(0.15, 0.35) * w;
        return fly(
          o.kind,
          o.svg(i),
          rand(o.size[0], o.size[1]),
          [
            { transform: `translate(${x0}px, -40px) rotate(0deg)`, opacity: 0 },
            { opacity: peak, offset: 0.08 },
            { opacity: peak, offset: 0.85 },
            { transform: `translate(${x1}px, ${h + 40}px) rotate(${rand(-o.spin, o.spin)}deg)`, opacity: 0 },
          ],
          { duration: rand(o.duration[0], o.duration[1]), delay: startAt(i, n), easing: 'linear' }
        );
      })
    );
  }

  /**
   * A group crossing the screen sideways inside a horizontal band. `top` and
   * `span` are fractions of the viewport height; `rise` is how far (px) each
   * item climbs over the trip.
   * @param {{ kind: string, svg: string, top: number, span: number, rightward: boolean, duration: [number, number], rise: number, opacity?: number, size?: [number, number], stagger?: number }} o
   */
  function cross(o) {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const n = COUNT();
    const peak = o.opacity ?? 0.95;
    const [lo, hi] = o.size ?? [22, 32];
    const scale = w < 750 ? 0.875 : 1;
    return Promise.all(
      lanes(n).map((lane, i) => {
        const size = rand(lo, hi) * scale;
        const y = Math.min(h * (o.top + lane * o.span), h - size - 6);
        const [x0, x1] = o.rightward ? [-50, w + 10] : [w + 10, -50];
        return fly(
          o.kind,
          o.svg,
          size,
          [
            { transform: `translate(${x0}px, ${y}px)`, opacity: 0 },
            { opacity: peak, offset: 0.06 },
            { opacity: peak, offset: 0.92 },
            { transform: `translate(${x1}px, ${y - o.rise}px)`, opacity: 0 },
          ],
          { duration: rand(o.duration[0], o.duration[1]), delay: startAt(i, n, o.stagger), easing: 'linear' }
        );
      })
    );
  }

  /* ---------- Scenes per month ---------- */

  const SEASONS = {
    october: [
      () => fall({ kind: 'leaf', svg: leafSvg, size: [18, 28], duration: [9000, 12000], spin: 360 }),
      () => cross({ kind: 'pumpkin', svg: PUMPKIN_SVG, top: 0.5, span: 0.4, rightward: true, duration: [12000, 15000], rise: 30 }),
      () => cross({ kind: 'ghost', svg: GHOST_SVG, top: 0.1, span: 0.42, rightward: false, duration: [22000, 26000], rise: 40, opacity: 0.9 }),
    ],
    november: [
      () => fall({ kind: 'acorn', svg: acornSvg, size: [16, 24], duration: [11000, 14000], spin: 200 }),
      () => cross({ kind: 'pie', svg: PIE_SVG, top: 0.5, span: 0.36, rightward: true, duration: [16000, 20000], rise: 20, size: [32, 40] }),
      () => cross({ kind: 'turkey', svg: TURKEY_SVG, top: 0.4, span: 0.45, rightward: false, duration: [16000, 24000], rise: 0, stagger: 10000 }),
    ],
    december: [
      () => fall({ kind: 'snow', svg: snowSvg, size: [14, 24], duration: [12000, 16000], spin: 180, opacity: 0.85 }),
      () => fall({ kind: 'hat', svg: () => HAT_SVG, size: [22, 30], duration: [12000, 16000], spin: 120, opacity: 0.95 }),
      () => fall({ kind: 'tree', svg: () => TREE_SVG, size: [22, 30], duration: [12000, 16000], spin: 30, opacity: 0.95 }),
    ],
  };

  function currentSeason() {
    if (forced) return forced;
    const now = Date.now();
    if (now < NOV_1) return 'october';
    if (now < DEC_1) return 'november';
    return 'december';
  }

  /** @param {string} season */
  const indexKey = (season) => `ruie-drift-next-${season}`;

  /** @param {string} season @param {number} count */
  function nextIndex(season, count) {
    try {
      const n = parseInt(sessionStorage.getItem(indexKey(season)) || '0', 10);
      return Number.isFinite(n) ? n % count : 0;
    } catch (e) {
      return 0;
    }
  }

  /** @param {string} season @param {number} n */
  function saveIndex(season, n) {
    try {
      sessionStorage.setItem(indexKey(season), String(n));
    } catch (e) {}
  }

  /* ---------- Scheduling ---------- */

  function overlayOpen() {
    return !!document.querySelector('dialog[open], header-drawer details[open]');
  }

  /** @param {number} ms */
  function schedule(ms) {
    try {
      sessionStorage.setItem(DUE_KEY, String(Date.now() + ms));
    } catch (e) {}
    setTimeout(tick, ms);
  }

  /** Time until the next scene: the saved due time, but never sooner than FIRST_DELAY after a page loads. */
  function firstWait() {
    if (fast) return FIRST_DELAY;
    try {
      const due = parseInt(sessionStorage.getItem(DUE_KEY) || '', 10);
      if (Number.isFinite(due)) return Math.min(INTERVAL, Math.max(FIRST_DELAY, due - Date.now()));
    } catch (e) {}
    return FIRST_DELAY;
  }

  function tick() {
    if (Date.now() >= END) return;
    if (running || document.hidden || overlayOpen()) {
      setTimeout(tick, RETRY);
      return;
    }

    const season = currentSeason();
    const scenes = SEASONS[season];
    const i = nextIndex(season, scenes.length);
    saveIndex(season, (i + 1) % scenes.length);
    running = true;
    scenes[i]().finally(() => {
      running = false;
    });
    schedule(INTERVAL);
  }

  // Freeze anything mid-flight while the tab is in the background.
  document.addEventListener('visibilitychange', () => {
    if (!layer) return;
    for (const anim of layer.getAnimations({ subtree: true })) {
      document.hidden ? anim.pause() : anim.play();
    }
  });

  setTimeout(tick, firstWait());
})();
